import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
    BarChart3,
    CheckCircle2,
    Circle,
    Clock,
    Coffee,
    Eye,
    FileText,
    Coins,
    DollarSign,
    LineChart,
    Loader2,
    Mail,
    Plus,
    X
} from "lucide-react";
import { getUserEmail } from "utils/localStorage";

import { useDemoMode } from "../../context/DemoModeContext";
import { useFeatureFlags } from "../../context/FeatureFlagContext";

import { fetchAttributionSummary } from "./RevenueAttribution/attributionReportApi";
import type { AttributionSetupStatus } from "./RevenueAttribution/attributionSetupApi";
import {
    deleteAttributionSetup,
    getAttributionSetup,
    saveAttributionSetup
} from "./RevenueAttribution/attributionSetupApi";
import ConnectionSettingsPanel from "./RevenueAttribution/ConnectionSettingsPanel";
import { COPY } from "./RevenueAttribution/copy";
import { getAttributionDemoData } from "./RevenueAttribution/fixtures/attribution-demo";
import { findPropertiesByIds } from "./RevenueAttribution/fixtures/oauth-demo";
import type { DevSyncState } from "./RevenueAttribution/fixtures/sync-demo";
import {
    DEV_SYNC_STATES,
    isSyncingDevState,
    relativeTime,
    SLOW_SYNC_MINUTES,
    syncStatusForDevState
} from "./RevenueAttribution/fixtures/sync-demo";
import { BetaFreeAccessBanner } from "./BetaFreeAccessBanner";
import type { Ga4SetupDetails } from "./RevenueAttribution/ga4Api";
import {
    airbyteRedirectUri,
    clearAirbyteSecretId,
    exchangeGa4Code,
    fetchGa4SetupDetails,
    initGa4AirbyteOAuth,
    isAirbyteSecretExpired,
    parseAirbyteSecretId,
    readRefreshToken,
    readStoredAirbyteSecretId,
    startGa4Sync,
    storeAirbyteSecretId,
    storeRefreshToken
} from "./RevenueAttribution/ga4Api";
import { ga4RedirectUri, verifyOAuthState } from "./RevenueAttribution/googleOAuthUrl";
import HowAttributionWorks from "./RevenueAttribution/HowAttributionWorks";
import RevenueAttributionReport from "./RevenueAttribution/RevenueAttributionReport";
import RevenueAttributionSetupWizardV2, {
    stepIndexOf,
    stepRowsFor
} from "./RevenueAttribution/RevenueAttributionSetupWizardV2";
import type { SetupState } from "./RevenueAttribution/setupState";
import {
    fromPersistedSetup,
    loadSetupState,
    mockDelay,
    saveSetupState,
    toPersistedSetup
} from "./RevenueAttribution/setupState";
import { SidePanel } from "./RevenueAttribution/SidePanel";
import { REVENUE_ATTRIBUTION_FEATURE_FLAG } from "./aiVisibilityFeatureFlags";

/** §2 — the three states of the Attribution tab. */
type ReportState = "NOT_CONFIGURED" | "SYNCING" | "CONFIGURED";

const STATE_PARAM = "demo_state";
/** Marks that we handed off to Airbyte's consent screen and expect a secret_id back. */
const AIRBYTE_PENDING_KEY = "citedby.revenueAttribution.airbytePending";
/** Step to return to after re-authorising, so the user lands where they left. */
const AIRBYTE_RETURN_STEP_KEY = "citedby.revenueAttribution.airbyteReturnStep";
const STATE_BY_PARAM: Record<string, ReportState> = {
    not_configured: "NOT_CONFIGURED",
    syncing: "SYNCING",
    configured: "CONFIGURED"
};
const PARAM_BY_STATE: Record<ReportState, AttributionSetupStatus> = {
    NOT_CONFIGURED: "not_configured",
    SYNCING: "syncing",
    CONFIGURED: "configured"
};

// ── Empty state (§3) ─────────────────────────────────────────────────────────

const PREVIEW_ICONS = [LineChart, BarChart3, FileText];

function EmptyStateCard({ onSetup, onWhatData }: { onSetup: () => void; onWhatData: () => void }) {
    return (
        <div className="w-full max-w-[520px] rounded-2xl border border-gray-200/70 bg-white/95 px-8 py-9 text-center shadow-xl backdrop-blur-sm">
            <div className="mx-auto flex h-11 w-11 items-center justify-center rounded-full bg-geekblue-100">
                <Coins className="h-5 w-5 text-geekblue-600" strokeWidth={2} />
            </div>
            <h2 className="mt-4 text-[17px] font-semibold tracking-tight text-gray-900">{COPY.empty.headline}</h2>
            <p className="mx-auto mt-2 max-w-[420px] text-[13px] leading-relaxed text-gray-500">{COPY.empty.body}</p>

            <ul className="mx-auto mt-6 max-w-[380px] space-y-2.5 text-left">
                {COPY.empty.previews.map((item, i) => {
                    const Icon = PREVIEW_ICONS[i] ?? LineChart;
                    return (
                        <li key={item} className="flex items-center gap-2.5">
                            <span className="flex h-6 w-6 flex-none items-center justify-center rounded-md bg-gray-100">
                                <Icon className="h-3.5 w-3.5 text-gray-500" strokeWidth={2} />
                            </span>
                            <span className="text-[12.5px] text-gray-700">{item}</span>
                        </li>
                    );
                })}
            </ul>

            <button
                type="button"
                onClick={onSetup}
                className="mt-7 rounded-lg bg-geekblue-600 px-5 py-2.5 text-[13px] font-medium text-white transition-colors hover:bg-geekblue-700">
                {COPY.empty.primaryCta}
            </button>
            <div className="mt-3">
                <button
                    type="button"
                    onClick={onWhatData}
                    className="text-[12.5px] font-medium text-geekblue-600 underline-offset-2 transition-colors hover:text-geekblue-700 hover:underline">
                    {COPY.empty.secondaryLink}
                </button>
            </div>
        </div>
    );
}

function RequirementsPanel({ open, onClose }: { open: boolean; onClose: () => void }) {
    const p = COPY.requirementsPanel;
    return (
        <SidePanel open={open} title={p.title} onClose={onClose}>
            <p className="text-[12.5px] text-gray-500">{p.intro}</p>
            <div className="mt-5 space-y-5">
                {p.items.map((item, i) => (
                    <div key={item.title} className="flex gap-3">
                        <span className="flex h-6 w-6 flex-none items-center justify-center rounded-full bg-geekblue-100 text-[11.5px] font-semibold text-geekblue-700">
                            {i + 1}
                        </span>
                        <div>
                            <h3 className="text-[13px] font-semibold text-gray-900">{item.title}</h3>
                            <p className="mt-1 text-[12.5px] leading-relaxed text-gray-600">{item.body}</p>
                        </div>
                    </div>
                ))}
            </div>
            <p className="mt-6 rounded-lg border-l-2 border-emerald-400 bg-emerald-50/60 px-3.5 py-2.5 text-[12.5px] font-medium leading-relaxed text-emerald-900">
                {p.noCode}
            </p>
        </SidePanel>
    );
}

// ── Syncing (§2) ─────────────────────────────────────────────────────────────

function SkeletonBlock({ className = "" }: { className?: string }) {
    return <div className={`animate-pulse rounded-xl bg-gray-100 ${className}`} />;
}

/** One row of the "what's happening" checklist — an honest, no-percentage progress. */
function PhaseRow({ status, label }: { status: "done" | "active" | "pending"; label: string }) {
    return (
        <li className="flex items-center gap-2.5">
            {status === "done" ? (
                <CheckCircle2 className="h-[15px] w-[15px] flex-none text-emerald-500" strokeWidth={2} />
            ) : status === "active" ? (
                <Loader2 className="h-[15px] w-[15px] flex-none animate-spin text-geekblue-600" strokeWidth={2} />
            ) : (
                <Circle className="h-[15px] w-[15px] flex-none text-gray-300" strokeWidth={2} />
            )}
            <span
                className={
                    status === "pending"
                        ? "text-[13px] text-gray-400"
                        : status === "active"
                        ? "text-[13px] font-medium text-gray-900"
                        : "text-[13px] text-gray-600"
                }>
                {label}
            </span>
        </li>
    );
}

/** One of the three "while you wait" notes: timing, notification, come-back-later. */
function WaitNote({ icon: Icon, label, body }: { icon: typeof Clock; label: string; body: string }) {
    return (
        <div className="flex gap-2.5">
            <Icon className="mt-0.5 h-4 w-4 flex-none text-gray-400" strokeWidth={2} />
            <div>
                <p className="text-[12.5px] font-medium text-gray-800">{label}</p>
                <p className="mt-0.5 text-[12.5px] leading-relaxed text-gray-500">{body}</p>
            </div>
        </div>
    );
}

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * The notification note, with a recipient editor. The connected account always
 * gets the "report ready" email (a fixed chip); the customer can add teammates
 * so nobody has to sit on this screen. Demo-local — no persistence yet.
 */
function NotifyRecipients({ owner }: { owner?: string | null }) {
    const c = COPY.syncing.notify;
    const [extra, setExtra] = useState<string[]>([]);
    const [adding, setAdding] = useState(false);
    const [value, setValue] = useState("");

    const candidate = value.trim().toLowerCase();
    const all = [...(owner ? [owner] : []), ...extra];
    const valid = EMAIL_RE.test(candidate) && !all.includes(candidate);

    const add = () => {
        if (!valid) return;
        setExtra((r) => [...r, candidate]);
        setValue("");
        setAdding(false);
    };

    return (
        <div className="flex gap-2.5">
            <Mail className="mt-0.5 h-4 w-4 flex-none text-gray-400" strokeWidth={2} />
            <div className="min-w-0 flex-1">
                <p className="text-[12.5px] font-medium text-gray-800">{c.label}</p>
                <p className="mt-0.5 text-[12.5px] leading-relaxed text-gray-500">
                    {all.length ? c.withRecipients : c.noEmail}
                </p>

                {all.length > 0 && (
                    <div className="mt-2 flex flex-wrap gap-1.5">
                        {owner && (
                            <span className="inline-flex max-w-full items-center rounded-md bg-gray-100 px-2 py-0.5 text-[11.5px] text-gray-700">
                                <span className="truncate">{owner}</span>
                            </span>
                        )}
                        {extra.map((e) => (
                            <span
                                key={e}
                                className="inline-flex max-w-full items-center gap-1 rounded-md bg-gray-100 px-2 py-0.5 text-[11.5px] text-gray-700">
                                <span className="truncate">{e}</span>
                                <button
                                    type="button"
                                    aria-label={`Remove ${e}`}
                                    onClick={() => setExtra((r) => r.filter((x) => x !== e))}
                                    className="flex-none text-gray-400 transition-colors hover:text-gray-700">
                                    <X className="h-3 w-3" strokeWidth={2.5} />
                                </button>
                            </span>
                        ))}
                    </div>
                )}

                {adding ? (
                    <div className="mt-2 flex items-center gap-1.5">
                        <input
                            type="email"
                            value={value}
                            autoFocus
                            onChange={(e) => setValue(e.target.value)}
                            onKeyDown={(e) => {
                                if (e.key === "Enter") add();
                                if (e.key === "Escape") {
                                    setAdding(false);
                                    setValue("");
                                }
                            }}
                            placeholder={c.placeholder}
                            className="min-w-0 flex-1 rounded-md border border-gray-200 px-2 py-1 text-[12px] text-gray-800 placeholder:text-gray-400 focus:border-geekblue-400 focus:outline-none"
                        />
                        <button
                            type="button"
                            onClick={add}
                            disabled={!valid}
                            className="flex-none rounded-md px-2.5 py-1 text-[11.5px] font-medium text-white transition-colors disabled:cursor-not-allowed disabled:opacity-40"
                            style={{ backgroundColor: "rgb(47,84,235)" }}>
                            {c.add}
                        </button>
                    </div>
                ) : (
                    <button
                        type="button"
                        onClick={() => setAdding(true)}
                        className="mt-1.5 inline-flex items-center gap-1 text-[12px] font-medium text-geekblue-600 transition-colors hover:text-geekblue-700">
                        <Plus className="h-3.5 w-3.5" strokeWidth={2.5} />
                        {c.addPeople}
                    </button>
                )}
            </div>
        </div>
    );
}

/**
 * §4 — the first-time-load screen. Setup is done; the first import is running.
 * The job is reassurance, not a progress bar: confirm setup landed, show the
 * phases honestly (no fake percentage), and make the three things a waiting
 * customer needs unmissable — it takes a while, we'll email you, you can leave.
 * Past the slow threshold the copy softens rather than turning into an error,
 * because a long sync is not a failed sync.
 */
function SyncingState({
    startedAt,
    slow,
    email,
    onOpenSetup,
    onDataLoaded
}: {
    startedAt: Date;
    slow: boolean;
    email?: string | null;
    /** Demo only — jump to the setup modal / the loaded report. */
    onOpenSetup?: () => void;
    onDataLoaded?: () => void;
}) {
    const [, tick] = useState(0);

    useEffect(() => {
        const id = window.setInterval(() => tick((n) => n + 1), 30_000);
        return () => window.clearInterval(id);
    }, []);

    const elapsedMinutes = (Date.now() - startedAt.getTime()) / 6e4;
    const isSlow = slow || elapsedMinutes > SLOW_SYNC_MINUTES;
    const c = COPY.syncing;

    return (
        <div className="space-y-6">
            <div className="mx-auto max-w-2xl rounded-2xl border border-gray-100 bg-white px-6 py-7 sm:px-9 sm:py-9">
                <div className="flex items-start gap-4">
                    <div className="flex h-10 w-10 flex-none items-center justify-center rounded-full bg-geekblue-100">
                        <Loader2 className="h-5 w-5 animate-spin text-geekblue-600" strokeWidth={2} />
                    </div>
                    <div className="min-w-0">
                        <p className="text-[11px] font-semibold uppercase tracking-wide text-emerald-600">
                            {c.eyebrow}
                        </p>
                        <h2 className="mt-0.5 text-[17px] font-semibold tracking-tight text-gray-900">{c.title}</h2>
                        <p className="mt-1 text-[13px] leading-relaxed text-gray-500">{c.lede}</p>
                        <p className="mt-1.5 text-[12px] text-gray-400">{c.started(relativeTime(startedAt))}</p>
                    </div>
                </div>

                <ul className="mt-6 space-y-2.5 border-t border-gray-100 pt-5">
                    <PhaseRow status="done" label={c.phases.connected} />
                    <PhaseRow status="active" label={c.phases.importing} />
                    <PhaseRow status="pending" label={c.phases.ready} />
                </ul>

                <div className="mt-6 grid items-start gap-4 border-t border-gray-100 pt-5 sm:grid-cols-3">
                    <WaitNote icon={Clock} label={c.timing.label} body={c.timing.body} />
                    <NotifyRecipients owner={email} />
                    <WaitNote icon={Coffee} label={c.leave.label} body={c.leave.body} />
                </div>

                {isSlow && (
                    <p className="mt-5 rounded-lg bg-amber-50 px-3.5 py-2.5 text-[12.5px] leading-relaxed text-amber-800">
                        {c.slow}
                    </p>
                )}

                {(onOpenSetup || onDataLoaded) && (
                    <div className="mt-6 flex flex-wrap items-center gap-2 border-t border-dashed border-gray-200 pt-4">
                        <span className="text-[10.5px] font-semibold uppercase tracking-wide text-gray-400">Demo</span>
                        {onOpenSetup && (
                            <button
                                type="button"
                                onClick={onOpenSetup}
                                className="rounded-lg border border-gray-200 bg-gray-50 px-2.5 py-1 text-[11.5px] font-medium text-gray-600 transition-colors hover:bg-gray-100">
                                Open setup modal
                            </button>
                        )}
                        {onDataLoaded && (
                            <button
                                type="button"
                                onClick={onDataLoaded}
                                className="rounded-lg border border-gray-200 bg-gray-50 px-2.5 py-1 text-[11.5px] font-medium text-gray-600 transition-colors hover:bg-gray-100">
                                Jump to loaded report
                            </button>
                        )}
                    </div>
                )}
            </div>

            {/* A muted preview so the wait has a shape — deliberately blurred, not broken. */}
            <div>
                <p className="mb-3 text-center text-[11px] font-medium uppercase tracking-wide text-gray-300">
                    {c.previewLabel}
                </p>
                <div aria-hidden className="pointer-events-none select-none opacity-50 blur-[1px]">
                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
                        {[0, 1, 2, 3].map((i) => (
                            <SkeletonBlock key={i} className="h-[92px]" />
                        ))}
                    </div>
                    <SkeletonBlock className="mt-4 h-[300px]" />
                </div>
            </div>
        </div>
    );
}

// ── Dev state switcher (§2) ──────────────────────────────────────────────────

function StateSwitcher({ value, onChange }: { value: ReportState; onChange: (next: ReportState) => void }) {
    return (
        <label className="inline-flex items-center gap-1.5 rounded-lg border border-dashed border-gray-300 bg-gray-50 px-2 py-1">
            <span className="text-[10.5px] font-semibold uppercase tracking-wide text-gray-400">State</span>
            <select
                aria-label="Demo state"
                value={value}
                onChange={(e) => onChange(e.target.value as ReportState)}
                className="bg-transparent text-[11.5px] font-medium text-gray-700 focus:outline-none">
                <option value="NOT_CONFIGURED">Not configured</option>
                <option value="SYNCING">Syncing</option>
                <option value="CONFIGURED">Configured</option>
            </select>
        </label>
    );
}

/** MOCK: forces each sync health state for review (§7). */
function SyncSwitcher({ value, onChange }: { value: DevSyncState; onChange: (next: DevSyncState) => void }) {
    return (
        <label className="inline-flex items-center gap-1.5 rounded-lg border border-dashed border-gray-300 bg-gray-50 px-2 py-1">
            <span className="text-[10.5px] font-semibold uppercase tracking-wide text-gray-400">Sync</span>
            <select
                aria-label="Demo sync state"
                value={value}
                onChange={(e) => onChange(e.target.value as DevSyncState)}
                className="bg-transparent text-[11.5px] font-medium text-gray-700 focus:outline-none">
                {DEV_SYNC_STATES.map((s) => (
                    <option key={s.value} value={s.value}>
                        {s.label}
                    </option>
                ))}
            </select>
        </label>
    );
}

// ── Non-demo blur + overlay (repo convention) ────────────────────────────────

/**
 * Non-demo overlay over the (blurred) report. Unlike demo mode — which is left entirely
 * unchanged — non-demo drives the real setup: it opens the wizard, whose result is persisted to
 * the backend. The report itself stays blurred because real attribution data only arrives after
 * the first sync; we never present the demo numbers as real.
 */

/**
 * Feature-flag gate for non-demo. When `ai_revenue_attribution` is off, the live experience is
 * withheld: the demo report is shown blurred behind a "Preview with Demo Data" card that flips the
 * whole app into demo mode. Demo mode itself is never gated. Follows the repo blur+overlay
 * convention (see AttributionModelTable / CLAUDE.md).
 */
function DemoPreviewOverlay({ report, onPreview }: { report: React.ReactNode; onPreview: () => void }) {
    return (
        <div className="relative">
            <div aria-hidden className="pointer-events-none select-none opacity-40 blur-[2px]">
                {report}
            </div>
            <div className="absolute inset-0 z-30 flex items-start justify-center px-4 pt-12">
                <div className="max-w-xs rounded-xl bg-white/95 p-6 text-center shadow-lg backdrop-blur-sm">
                    <div
                        className="mx-auto mb-3 flex h-10 w-10 items-center justify-center rounded-full"
                        style={{ backgroundColor: "rgba(47,84,235,0.08)" }}>
                        <Eye className="h-5 w-5" style={{ color: "rgb(47,84,235)" }} />
                    </div>
                    <h3 className="mb-1.5 text-sm font-semibold text-gray-900">{COPY.page.title}</h3>
                    <p className="mb-4 text-xs text-gray-500">
                        Revenue Attribution isn&apos;t enabled for your workspace yet. Switch to Demo Mode to preview it
                        with sample data.
                    </p>
                    <button
                        type="button"
                        onClick={() => onPreview()}
                        className="inline-flex items-center gap-1.5 rounded-lg px-4 py-2 text-xs font-medium text-white transition-colors"
                        style={{ backgroundColor: "rgb(47,84,235)" }}
                        onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "rgb(25,40,190)")}
                        onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "rgb(47,84,235)")}>
                        <Eye className="h-3.5 w-3.5" />
                        Preview with Demo Data
                    </button>
                </div>
            </div>
        </div>
    );
}

// ── Page ─────────────────────────────────────────────────────────────────────

const RevenueAttributionPage: React.FC = () => {
    const { isDemoMode, toggleDemoMode } = useDemoMode();
    const { getFlag, isLoading: flagsLoading } = useFeatureFlags();
    // `ai_revenue_attribution` gates the live (non-demo) experience only; demo is always available.
    const featureEnabled = getFlag(REVENUE_ATTRIBUTION_FEATURE_FLAG);
    const demoPreviewOnly = !isDemoMode && !featureEnabled;
    const [searchParams, setSearchParams] = useSearchParams();

    // §1 — the demo_state param is a demo-only review aid; it is never read or written in
    // non-demo, where the report state comes from the backend setup status.
    const paramState = isDemoMode ? STATE_BY_PARAM[(searchParams.get(STATE_PARAM) || "").toLowerCase()] : undefined;
    // §2 — demo defaults to CONFIGURED so first load shows the report; ?demo_state= still overrides.
    // Non-demo starts NOT_CONFIGURED until the backend setup status resolves.
    const [state, setState] = useState<ReportState>(paramState ?? (isDemoMode ? "CONFIGURED" : "NOT_CONFIGURED"));
    const [wizardOpen, setWizardOpen] = useState(false);
    const [requirementsOpen, setRequirementsOpen] = useState(false);
    const [setup, setSetup] = useState<SetupState>(() => loadSetupState());
    // Non-demo persistence state. In demo mode these stay inert — demo never hits the network.
    const [setupLoading, setSetupLoading] = useState(!isDemoMode);
    const [savedSetupExists, setSavedSetupExists] = useState(false);

    // Latched once at first render: is this page-load an OAuth/Airbyte return leg? The return-leg
    // effects clear the URL params and the pending flag as they run, and the setup-load effect can
    // re-fire later (featureEnabled resolves async) — so a transient re-check would miss it and let
    // the backend load clobber the just-restored wizard step. A ref computed once survives that.
    const isOAuthReturnRef = useRef<boolean>(
        (() => {
            if (typeof window === "undefined") return false;
            const p = new URLSearchParams(window.location.search);
            if (p.has("code") || p.has("secret_id") || p.has("error")) return true;
            try {
                return sessionStorage.getItem(AIRBYTE_PENDING_KEY) === "1";
            } catch {
                return false;
            }
        })()
    );

    const data = useMemo(() => getAttributionDemoData(), []);

    // Non-demo: load the saved setup from the backend and reflect it. Re-runs when the app is
    // toggled into/out of demo mode. Demo mode keeps its localStorage-only behaviour untouched.
    useEffect(() => {
        if (isDemoMode) {
            setSetupLoading(false);
            return;
        }
        // Flag off → the live experience is withheld; skip the backend call and let the
        // demo-preview overlay take over. Re-runs and fetches once the flag resolves on.
        if (!featureEnabled) {
            setSetupLoading(false);
            return;
        }
        // An OAuth/Airbyte return leg owns the wizard state on this page-load: the ?code= /
        // ?secret_id= handlers restore the exact step and connection the user left. The backend
        // load resolves later and, via fromPersistedSetup, resets step to 1 and overwrites
        // connected/propertyIds with the last-saved (pre-connect) blob — which clobbered the
        // just-authorised sync and bounced the wizard back to step 1. Skip the fetch on a return
        // leg; localStorage already holds the in-progress config, and normal loads still fetch.
        if (isOAuthReturnRef.current) {
            setSetupLoading(false);
            return;
        }
        let cancelled = false;
        setSetupLoading(true);
        getAttributionSetup()
            .then((res) => {
                if (cancelled || !res) return;
                if (res.setup) {
                    const loaded = fromPersistedSetup(res.setup);
                    setSetup(loaded);
                    saveSetupState(loaded);
                    setSavedSetupExists(true);
                    // Honour the persisted status: a first sync that's still running
                    // restores the first-time-load screen, not an empty report.
                    const restored = (res.status && STATE_BY_PARAM[res.status]) || "CONFIGURED";
                    if (restored === "SYNCING" && res.updatedAt) setSyncStartedAt(new Date(res.updatedAt));
                    setState(restored);
                } else {
                    setSavedSetupExists(false);
                    setState("NOT_CONFIGURED");
                }
            })
            .catch(() => {
                /* offline / server down — leave the local defaults in place */
            })
            .finally(() => {
                if (!cancelled) setSetupLoading(false);
            });
        return () => {
            cancelled = true;
        };
    }, [isDemoMode, featureEnabled]);

    /**
     * Flow B return leg. Airbyte sends the browser back with ?secret_id=… once it
     * has completed Google's consent. The secret is stored rather than spent here:
     * provisioning happens on the final step, once the rest of the wizard is done.
     */
    useEffect(() => {
        const secretId = parseAirbyteSecretId(window.location.search);
        let pending = false;
        try {
            pending = sessionStorage.getItem(AIRBYTE_PENDING_KEY) === "1";
        } catch {
            /* storage blocked — fall back to the presence of secret_id alone */
        }
        if (!secretId && !pending) return;

        try {
            sessionStorage.removeItem(AIRBYTE_PENDING_KEY);
        } catch {
            /* no-op */
        }

        const params = new URLSearchParams(searchParams);
        ["secret_id", "state"].forEach((k) => params.delete(k));
        setSearchParams(params, { replace: true });

        // Land on whichever step launched the authorisation, not always step 1 —
        // re-authorising mid-wizard should return you where you were.
        const rows = stepRowsFor("ga4", isDemoMode);
        let returnStep = stepIndexOf(rows, "connect");
        try {
            const saved = Number(sessionStorage.getItem(AIRBYTE_RETURN_STEP_KEY));
            if (Number.isFinite(saved) && saved >= 1 && saved <= rows.length) returnStep = saved;
            sessionStorage.removeItem(AIRBYTE_RETURN_STEP_KEY);
        } catch {
            /* no-op — fall back to the connect step */
        }
        patch({ setupPath: "ga4", step: returnStep });
        setWizardOpen(true);
        setAuthorising(false);
        setSecretExpired(false);

        if (!secretId) {
            setOauthError("Google didn't return an authorisation for syncing. Please try authorising again.");
            return;
        }

        storeAirbyteSecretId(secretId);
        setAirbyteSecretId(secretId);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    /**
     * Return leg of the Google redirect. Google sends the browser back here with
     * ?code= (or ?error=). We verify the nonce we issued, hand the code to the
     * backend to exchange for tokens, then strip the params so a refresh can't
     * replay a one-time code.
     */
    useEffect(() => {
        const code = searchParams.get("code");
        const oauthError = searchParams.get("error");
        if (!code && !oauthError) return;

        const stripParams = () => {
            const params = new URLSearchParams(searchParams);
            ["code", "error", "state", "scope", "authuser", "prompt"].forEach((k) => params.delete(k));
            setSearchParams(params, { replace: true });
        };

        // Resume on the Connect step, not step 1 — step 1 is the path chooser.
        const resumeStep = stepIndexOf(stepRowsFor("ga4", isDemoMode), "connect");
        patch({ setupPath: "ga4", step: resumeStep });
        setWizardOpen(true);

        if (oauthError) {
            setOauthError(
                oauthError === "access_denied"
                    ? COPY.wizard.oauth.errors.DENIED.message
                    : `Google returned an error: ${oauthError}`
            );
            stripParams();
            return;
        }

        if (!verifyOAuthState(searchParams.get("state"))) {
            setOauthError("That sign-in didn't match the request we started. Please try again.");
            stripParams();
            return;
        }

        const redirectUri = ga4RedirectUri();
        // Strip before awaiting: an authorization code is single-use, and leaving
        // it in the URL means a refresh retries an already-spent code.
        stripParams();

        setExchanging(true);
        exchangeGa4Code(code as string, redirectUri).then((res) => {
            setExchanging(false);
            if (!res.ok) {
                setOauthError(res.message);
                return;
            }
            // Google's consent screen lets users untick individual scopes, so a
            // successful exchange does not mean we were granted what we asked for.
            if (!res.data.hasAnalyticsScope) {
                setOauthError(COPY.wizard.oauth.errors.DENIED.message);
                return;
            }
            storeRefreshToken(res.data.refreshToken);
            patch({ connected: true });

            // First call, no propertyId: returns the account/property tree only.
            setDetailsLoading(true);
            void fetchGa4SetupDetails({ refreshToken: res.data.refreshToken }).then((probe) => {
                setDetailsLoading(false);
                if (!probe.ok) {
                    setOauthError(probe.message);
                    return;
                }
                setSetupDetails(probe.data);
            });
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // ?demo_state= wins whenever it changes, so review links land where they say.
    useEffect(() => {
        if (paramState && paramState !== state) setState(paramState);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [paramState]);

    const patch = useCallback((p: Partial<SetupState>) => {
        setSetup((prev) => {
            const next = { ...prev, ...p };
            saveSetupState(next);
            return next;
        });
    }, []);

    const goToState = useCallback(
        (next: ReportState) => {
            setState(next);
            // §1 — only demo mirrors the state into the URL (?demo_state=), so review links
            // land where they say. Non-demo must never leak this param into the address bar.
            if (!isDemoMode) return;
            const params = new URLSearchParams(searchParams);
            params.set(STATE_PARAM, PARAM_BY_STATE[next]);
            setSearchParams(params, { replace: true });
        },
        [isDemoMode, searchParams, setSearchParams]
    );

    // Step 6's "Go to report". Demo: mocked SYNCING → CONFIGURED, no network (unchanged).
    // Non-demo: persist the sanitized config to the backend, then reflect it.
    const finishSetup = useCallback(async () => {
        setWizardOpen(false);
        if (isDemoMode) {
            setSyncStartedAt(new Date());
            goToState("SYNCING");
            await mockDelay(3000);
            goToState("CONFIGURED");
            return;
        }
        try {
            // The first Airbyte sync has just been kicked off, so the report isn't
            // ready yet — persist "syncing" and show the first-time-load screen. The
            // poll below flips to CONFIGURED once the backend marks the sync done.
            await saveAttributionSetup(toPersistedSetup(setup), "syncing");
            setSavedSetupExists(true);
            setSyncStartedAt(new Date());
            setState("SYNCING");
        } catch {
            /* server down — the config is still in localStorage, so nothing the user entered is lost */
        }
    }, [isDemoMode, goToState, setup]);

    // Live: while the first sync runs, poll for readiness and flip to the report once data
    // has actually landed.
    //
    // We do NOT poll the setup blob's status: the backend only ever stores the status we last
    // saved (see attributionSetupApi / revenue_attribution_setup.go) — nothing server-side
    // flips "syncing" → "configured", so that poll can never succeed. The report itself is the
    // authority: /summary returns configured with no_data=false once the first import has rows.
    // When that happens we flip the UI and write status=configured back, so a reload — and the
    // stored row — reflect reality instead of staying stuck on "syncing".
    useEffect(() => {
        if (isDemoMode || state !== "SYNCING") return;
        let cancelled = false;
        const poll = () =>
            fetchAttributionSummary({ attributionScope: "session" })
                .then((res) => {
                    if (cancelled || !res.ok) return;
                    const meta = res.data.meta;
                    if (meta.configured && !meta.no_data) {
                        setState("CONFIGURED");
                        void saveAttributionSetup(toPersistedSetup(setup), "configured").catch(() => {
                            /* the report is already live; the status write is best-effort */
                        });
                    }
                })
                .catch(() => {
                    /* transient — keep waiting, the next tick retries */
                });
        poll();
        const id = window.setInterval(poll, 15_000);
        return () => {
            cancelled = true;
            window.clearInterval(id);
        };
    }, [isDemoMode, state, setup]);

    // Poll the stored setup status every 30s: once it reads "configured", drop the first-time
    // loader and show the report — whether the first sync finished server-side or the status was
    // set manually. Read-only, so it can't clobber the stored row; it only ever moves forward to
    // CONFIGURED and does nothing otherwise. Paused while the wizard is open.
    const reportStateRef = useRef(state);
    reportStateRef.current = state;
    useEffect(() => {
        if (isDemoMode || !featureEnabled || wizardOpen) return;
        let cancelled = false;
        const checkStatus = () =>
            getAttributionSetup()
                .then((res) => {
                    if (cancelled || !res?.setup) return;
                    if (res.status === "configured" && reportStateRef.current !== "CONFIGURED") {
                        const loaded = fromPersistedSetup(res.setup);
                        setSetup(loaded);
                        saveSetupState(loaded);
                        setSavedSetupExists(true);
                        setState("CONFIGURED");
                    }
                })
                .catch(() => {
                    /* transient — the next tick retries */
                });
        const id = window.setInterval(checkStatus, 30_000);
        return () => {
            cancelled = true;
            window.clearInterval(id);
        };
    }, [isDemoMode, featureEnabled, wizardOpen]);

    // §4 — persist partial progress as each step completes, not only at finishSetup. The status
    // mirrors the current report state, so an in-progress setup saves "not_configured" (the
    // report stays gated) while editing a live setup keeps it "configured" (no downgrade). Fires
    // only when completedSteps grows, keeping this to one write per step.
    //
    // Only persist growth that happens INSIDE an open wizard, and never before the backend status
    // has resolved. On a plain mount/refresh the restored setup already carries completedSteps, and
    // `state` is still the initial NOT_CONFIGURED until the async load resolves (and on an OAuth
    // return the load is skipped entirely) — without these guards the effect mistook the restore
    // for growth and wrote not_configured back, clobbering a configured/syncing row. The final step
    // and the sync-create are persisted explicitly by finishSetup / handleCreateConnection, so
    // gating on wizardOpen loses no progress. persistedCompletedLen is seeded from the restored
    // length so reopening the wizard on an existing setup isn't seen as a fresh step either.
    const persistedCompletedLen = useRef<number | null>(null);
    useEffect(() => {
        if (isDemoMode || !featureEnabled) return;
        // Wait until the backend status has resolved — until then `state` is the initial
        // NOT_CONFIGURED and any write here would be against an unknown row.
        if (setupLoading) return;
        const len = setup.completedSteps.length;
        // Seed the baseline on the first settled render, without writing, so a restored
        // completedSteps is never mistaken for growth.
        if (persistedCompletedLen.current === null) {
            persistedCompletedLen.current = len;
            return;
        }
        if (len <= persistedCompletedLen.current) {
            persistedCompletedLen.current = len;
            return;
        }
        // Growth detected. Absorb it into the baseline either way, but only PERSIST it when it
        // happened inside the open wizard — the final step and the sync-create are written
        // explicitly by finishSetup / handleCreateConnection, so a growth seen with the wizard
        // closed (e.g. a mount/refresh restoring progress) must not write and downgrade a
        // configured/syncing row back to not_configured.
        persistedCompletedLen.current = len;
        if (!wizardOpen) return;
        void saveAttributionSetup(toPersistedSetup(setup), PARAM_BY_STATE[state]).catch(() => {
            /* best-effort — localStorage still holds the in-progress config */
        });
    }, [isDemoMode, featureEnabled, setup, state, setupLoading, wizardOpen]);

    // Sync health is dev-forced in demo; the SYNCING page state and the sync
    // switcher stay in step with each other.
    const devSync = setup.devSyncState;
    const syncStatus = useMemo(() => syncStatusForDevState(devSync), [devSync]);
    const selectedProperties = findPropertiesByIds(setup.propertyIds);
    const propertyIds = selectedProperties.map((p) => p.propertyId);
    const [syncStartedAt, setSyncStartedAt] = useState<Date>(() => new Date());
    const [connectionOpen, setConnectionOpen] = useState(false);
    /** Surfaced by the wizard's step 1 after the Google round trip. */
    const [oauthError, setOauthError] = useState<string | null>(null);
    const [exchanging, setExchanging] = useState(false);
    /** Probe results — accounts first, then everything once a property is chosen. */
    const [setupDetails, setSetupDetails] = useState<Ga4SetupDetails | null>(null);
    const [detailsLoading, setDetailsLoading] = useState(false);
    const [syncing, setSyncing] = useState(false);
    /** Distinct from syncStartedAt, which times the SYNCING skeleton. */
    const [ingestionStartedAt, setIngestionStartedAt] = useState<string | null>(null);
    const [connectionExisted, setConnectionExisted] = useState(false);
    /** Flow B handle, obtained on step 1 and spent on the final step. */
    const [airbyteSecretId, setAirbyteSecretId] = useState<string | null>(() => readStoredAirbyteSecretId());
    const [authorising, setAuthorising] = useState(false);
    const [secretExpired, setSecretExpired] = useState(false);

    /**
     * The secret is obtained on step 1 and spent on the last one, so it can go
     * stale mid-wizard. Poll rather than checking on render alone: someone can sit
     * on a step for minutes without React re-rendering.
     */
    useEffect(() => {
        if (isDemoMode) return;
        const check = () => {
            const expired = isAirbyteSecretExpired();
            setSecretExpired(expired);
            if (expired) {
                clearAirbyteSecretId();
                setAirbyteSecretId(null);
            }
        };
        check();
        const id = window.setInterval(check, 15_000);
        return () => window.clearInterval(id);
    }, [isDemoMode, airbyteSecretId]);

    /**
     * Creates the Airbyte source/connection and starts the first sync. Demo mode
     * simulates it — the repo convention is that demo never touches the network.
     */
    /**
     * Flow B, launched from step 1. Airbyte runs this consent itself and hands
     * back a secret_id; the refreshToken from Flow A can't provision.
     */
    const handleAuthoriseSync = useCallback(async () => {
        if (authorising) return;
        setAuthorising(true);

        if (isDemoMode) {
            await mockDelay(1200);
            setAuthorising(false);
            setAirbyteSecretId("demo-secret");
            return;
        }

        const res = await initGa4AirbyteOAuth(airbyteRedirectUri());
        if (!res.ok) {
            setAuthorising(false);
            setOauthError(res.message);
            return;
        }
        try {
            sessionStorage.setItem(AIRBYTE_PENDING_KEY, "1");
            sessionStorage.setItem(AIRBYTE_RETURN_STEP_KEY, String(setup.step));
        } catch {
            /* storage blocked — the callback still carries secret_id */
        }
        window.location.href = res.data.consentUrl;
    }, [authorising, isDemoMode, setup.step]);

    /** Final step: spend the secret and create the Airbyte source + connection. */
    const handleCreateConnection = useCallback(async () => {
        if (syncing) return;
        setSyncing(true);

        if (isDemoMode) {
            await mockDelay(1500);
            setSyncing(false);
            setIngestionStartedAt(new Date().toISOString());
            return;
        }

        const secretId = airbyteSecretId ?? readStoredAirbyteSecretId();
        if (!secretId) {
            setSyncing(false);
            setOauthError("Syncing isn't authorised yet. Go back to step 1 and authorise it.");
            return;
        }

        const res = await startGa4Sync({ propertyIds: setup.propertyIds, secretId });
        setSyncing(false);
        if (!res.ok) {
            // A stale handle reads as "secret was not found in the store" — the fix
            // is re-authorising on step 1, not retrying with the same secret.
            setOauthError(res.message);
            return;
        }
        clearAirbyteSecretId();
        setAirbyteSecretId(null);
        setIngestionStartedAt(res.data.syncStartedAt);
        setConnectionExisted(res.data.connectionExist);
        // The sync now exists server-side. Persist "syncing" immediately — not only at the
        // final Finish click — so a user who closes the tab here can't strand the row on
        // not_configured behind a live connection (nothing server-side promotes it, and the
        // /summary poll only runs from the SYNCING state). On next load the syncing status
        // restores the first-load screen and the poll flips it to configured once data lands.
        void saveAttributionSetup(toPersistedSetup(setup), "syncing").catch(() => {
            /* best-effort — finishSetup writes syncing again, and localStorage holds the config */
        });
    }, [syncing, isDemoMode, airbyteSecretId, setup]);

    /**
     * Second call, with a propertyId: runs every probe so the channel, key-event
     * and revenue steps can be driven by real data rather than fixtures.
     */
    const loadPropertyDetails = useCallback((propertyId: string, forceRefresh = false) => {
        const refreshToken = readRefreshToken();
        if (!refreshToken) {
            setOauthError("Your Google session expired. Reconnect to continue.");
            return;
        }
        setDetailsLoading(true);
        void fetchGa4SetupDetails({ refreshToken, propertyId, forceRefresh }).then((probe) => {
            setDetailsLoading(false);
            if (!probe.ok) {
                setOauthError(probe.message);
                return;
            }
            setSetupDetails(probe.data);
        });
    }, []);

    /**
     * Reopening a completed setup lands on "Value per conversion" — deal value is
     * the one number customers revise, and Back/Continue reach every other step.
     * Without this the wizard would resume on step 6, which is a dead end.
     */
    const openSetupForEditing = useCallback(() => {
        patch({ step: 4 });
        setWizardOpen(true);
    }, [patch]);

    /** §6 — "Fix now" on the skipped-fix banner reopens step 2. */
    const reopenChannelStep = useCallback(() => {
        patch({ step: 2, channelFixed: false, channelOutcome: null, channelAutoAdvanced: false });
        setWizardOpen(true);
    }, [patch]);

    // §7 — the report-ready email defaults to the logged-in user in live (the connected Google
    // account isn't captured there, so setup.googleAccountEmail is null); demo keeps the mocked
    // connected account.
    const defaultNotifyEmail = isDemoMode ? setup.googleAccountEmail : getUserEmail();

    const report = (
        <RevenueAttributionReport
            data={data}
            currency={setup.currency}
            ga4Revenue={setup.devGa4Revenue}
            channelSkipped={setup.channelSkipped}
            onFixChannel={reopenChannelStep}
            syncStatus={syncStatus}
            propertyIds={propertyIds}
            onReconnect={() => {
                patch({ step: 1 });
                setWizardOpen(true);
            }}
            onOpenConnection={() => setConnectionOpen(true)}
            onEditSetup={openSetupForEditing}
            onConfiguredChange={(configured) => {
                // The report API is the authority on whether setup actually landed;
                // a saved config with no connection behind it still means "not set up".
                if (!configured && !isDemoMode) goToState("NOT_CONFIGURED");
            }}
        />
    );

    const body = (() => {
        // Wait for the flag before deciding the non-demo experience, so an enabled workspace
        // never flashes the demo-preview overlay. Demo mode doesn't depend on the flag.
        if (!isDemoMode && flagsLoading) {
            return (
                <div className="flex items-center justify-center gap-2 py-24 text-[12.5px] text-gray-500">
                    <Loader2 className="h-4 w-4 animate-spin text-geekblue-500" />
                    {COPY.page.title}…
                </div>
            );
        }
        // Flag off in non-demo → only the demo preview is offered.
        if (demoPreviewOnly) {
            return <DemoPreviewOverlay report={report} onPreview={() => toggleDemoMode()} />;
        }
        if (setupLoading) {
            return (
                <div className="flex items-center justify-center gap-2 py-24 text-[12.5px] text-gray-500">
                    <Loader2 className="h-4 w-4 animate-spin text-geekblue-500" />
                    Loading your attribution setup…
                </div>
            );
        }
        if (state === "SYNCING") {
            return (
                <SyncingState
                    startedAt={syncStartedAt}
                    slow={devSync === "SYNCING_SLOW"}
                    email={defaultNotifyEmail}
                    onOpenSetup={isDemoMode ? () => setWizardOpen(true) : undefined}
                    onDataLoaded={isDemoMode ? () => goToState("CONFIGURED") : undefined}
                />
            );
        }
        if (state === "CONFIGURED") return report;
        // NOT_CONFIGURED — the report sits blurred behind the setup card, so the
        // customer can see the thing they're being asked to connect data for.
        return (
            <div className="relative">
                <div aria-hidden className="pointer-events-none select-none opacity-45 blur-[3px]">
                    {report}
                </div>
                <div className="absolute inset-0 z-30 flex items-start justify-center px-4 pt-12">
                    <EmptyStateCard onSetup={() => setWizardOpen(true)} onWhatData={() => setRequirementsOpen(true)} />
                </div>
            </div>
        );
    })();

    return (
        <div className="relative flex-1 min-h-0 overflow-y-auto">
            <div className="mx-auto w-full max-w-[1500px] px-6 py-8">
                <div className="mb-5 flex flex-wrap items-start justify-between gap-3">
                    <div className="flex items-center gap-2">
                        <DollarSign className="h-5 w-5 text-geekblue-600" strokeWidth={2} />
                        <div>
                            <h1 className="text-[22px] font-semibold tracking-tight text-gray-900">
                                {COPY.page.title}
                            </h1>
                            <p className="text-[12.5px] text-gray-500">{COPY.page.subtitle}</p>
                        </div>
                    </div>
                    <div className="flex items-center gap-3">
                        {/* Explanation lives behind a link so the tab leads with the report. */}
                        <HowAttributionWorks reportState={state} onSetup={() => setWizardOpen(true)} />
                        {/* Dev-only switchers — demo mode only, per §2 and §7. */}
                        {isDemoMode && <StateSwitcher value={state} onChange={goToState} />}
                        {isDemoMode && (
                            <SyncSwitcher
                                value={devSync}
                                onChange={(next) => {
                                    patch({ devSyncState: next });
                                    if (isSyncingDevState(next)) {
                                        setSyncStartedAt(
                                            // Backdate so the slow-sync copy is reachable on demand.
                                            next === "SYNCING_SLOW"
                                                ? new Date(Date.now() - (SLOW_SYNC_MINUTES + 20) * 6e4)
                                                : new Date()
                                        );
                                        goToState("SYNCING");
                                    } else {
                                        goToState("CONFIGURED");
                                    }
                                }}
                            />
                        )}
                    </div>
                </div>

                {/* Beta notice sits above everything, matching the ChatGPT Ads pattern. */}
                <BetaFreeAccessBanner className="mb-5" />

                {body}
            </div>

            {wizardOpen && (
                <RevenueAttributionSetupWizardV2
                    state={setup}
                    patch={patch}
                    oauthError={oauthError}
                    exchanging={exchanging}
                    setupDetails={setupDetails}
                    detailsLoading={detailsLoading}
                    onSelectProperty={loadPropertyDetails}
                    onAuthoriseSync={handleAuthoriseSync}
                    authorising={authorising}
                    airbyteAuthorised={Boolean(airbyteSecretId) && !secretExpired}
                    secretExpired={secretExpired}
                    onStartSync={handleCreateConnection}
                    syncing={syncing}
                    syncStartedAt={ingestionStartedAt}
                    connectionExisted={connectionExisted}
                    onDismissOauthError={() => setOauthError(null)}
                    defaultNotifyEmail={defaultNotifyEmail}
                    onClose={() => setWizardOpen(false)}
                    onFinish={finishSetup}
                />
            )}

            <RequirementsPanel open={requirementsOpen} onClose={() => setRequirementsOpen(false)} />

            <ConnectionSettingsPanel
                open={connectionOpen}
                onClose={() => setConnectionOpen(false)}
                setup={setup}
                syncStatus={syncStatus}
                onChangeProperties={() => {
                    setConnectionOpen(false);
                    patch({ step: 1 });
                    setWizardOpen(true);
                }}
                onDisconnect={() => {
                    setConnectionOpen(false);
                    patch({ connected: false, propertyIds: [], googleAccountEmail: null, step: 1 });
                    if (isDemoMode) {
                        goToState("NOT_CONFIGURED");
                    } else {
                        setSavedSetupExists(false);
                        setState("NOT_CONFIGURED");
                        void deleteAttributionSetup().catch(() => {
                            /* server down — local state already reset; next save re-syncs */
                        });
                    }
                }}
            />
        </div>
    );
};

export default RevenueAttributionPage;
